import { usePreferredDark, useStorage } from '@vueuse/core'
import { computed, watch } from 'vue'

export type ResolvedAppearance = 'light' | 'dark'
export type Appearance = ResolvedAppearance | 'system'

const setCookie = (name: string, value: string, days = 365) => {
  if (typeof document === 'undefined') {
    return
  }

  const maxAge = days * 24 * 60 * 60

  document.cookie = `${name}=${value};path=/;max-age=${maxAge};SameSite=Lax`
}

const getInitialAppearance = (): Appearance => {
  if (typeof window === 'undefined') {
    return 'system'
  }

  try {
    const rawStored =
      localStorage.getItem('appearance') ||
      localStorage.getItem('vueuse-color-scheme')
    if (rawStored === 'auto' || rawStored === 'system') {
      return 'system'
    }
    if (rawStored === 'light' || rawStored === 'dark') {
      return rawStored
    }
  } catch {
    // Ignore localStorage access restrictions
  }

  return 'system'
}

export const appearance = useStorage<Appearance>(
  'appearance',
  getInitialAppearance(),
)

// Normalize 'auto' or invalid values stored in localStorage to 'system'
if (
  appearance.value !== 'light' &&
  appearance.value !== 'dark' &&
  appearance.value !== 'system'
) {
  appearance.value = 'system'
}

export const preferredDark = usePreferredDark()

export const resolvedAppearance = computed<ResolvedAppearance>(() => {
  if (appearance.value === 'system') {
    return preferredDark.value ? 'dark' : 'light'
  }

  return appearance.value === 'dark' ? 'dark' : 'light'
})

export function applyTheme(resolved: ResolvedAppearance) {
  if (typeof document === 'undefined') {
    return
  }

  const isDark = resolved === 'dark'
  const root = document.documentElement

  root.classList.toggle('dark', isDark)
  root.classList.toggle('light', !isDark)
  root.style.colorScheme = isDark ? 'dark' : 'light'
}

export function updateTheme(value: Appearance) {
  appearance.value = value
  setCookie('appearance', value)
  applyTheme(resolvedAppearance.value)
}

export function initializeTheme() {
  if (typeof document !== 'undefined') {
    applyTheme(resolvedAppearance.value)
    setCookie('appearance', appearance.value)
  }
}

// Reactive sync to DOM and cookie
if (typeof window !== 'undefined') {
  watch(
    resolvedAppearance,
    (val) => {
      applyTheme(val)
    },
    { immediate: true },
  )

  watch(appearance, (val) => {
    setCookie('appearance', val)
  })
}

export const colorMode = Object.assign(
  computed({
    get: () => appearance.value,
    set: (val: Appearance) => {
      updateTheme(val)
    },
  }),
  {
    system: computed(() => (preferredDark.value ? 'dark' : 'light')),
    state: resolvedAppearance,
  },
)

export function useAppearance() {
  function updateAppearance(value: Appearance) {
    updateTheme(value)
  }

  return {
    appearance,
    resolvedAppearance,
    updateAppearance,
  }
}
